import { ObjectId } from '@racemap/sdk/schema/base';
import {
  type EntityEvent,
  EntityEventSchema,
  EntityEventState,
  type EntityEventStateWithoutNew,
  EntityType,
} from '@racemap/sdk/schema/entityEvents';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { sleep } from '@racemap/utilities/functions/utils';
import type { RacemapStarter } from '@racemap/utilities/types/events';
import * as Comlink from 'comlink';
import { produce } from 'immer';
import type mapboxgl from 'mapbox-gl';
import { basicAlertEventTypes } from 'src/components/MonitorBeta/Alerts';
import type { SelectedStarterAlertMenu } from 'src/components/MonitorBeta/StarterAlertsMenu';
import type { SelectedStarterGraphInfoMenu } from 'src/components/MonitorBeta/StarterGraphInfoMenu';
import type { RefinedPoint, StarterTracksManagement } from 'src/worker/starter-tracks-management';
import { z } from 'zod';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../reducers';
import { showAlertNotification } from './showAlertNotification';

const apiClient = RacemapAPIClient.fromWindowLocation();

interface Target {
  center: mapboxgl.FlyToOptions['center'];
  zoom: mapboxgl.FlyToOptions['zoom'];
}

interface MonitorActions {
  setEventId: (eventIdOrSlug: string | ObjectId) => Promise<void>;
  initStarterTracksManagement: () => Promise<void>;
  closeStarterTracksManagement: () => Promise<void>;
  updateStarterTracks: () => Promise<void>;
  getStarterTrack: (starterId: string) => Promise<Array<RefinedPoint>>;
  setSelectedStarterId: (starterId: string | null, flyToStarterPosition?: boolean) => Promise<void>;
  setMapInstance: (mapInstance: mapboxgl.Map | null) => void;
  flyTo: (target: Target) => Promise<void>;
  getFrontendGeneratedEntityEvents: () => Promise<Map<string, EntityEvent>>;
  loadAlerts: () => Promise<void>;
  initMonitoring: (eventSlug: string) => Promise<void>;
  updateAlertState: (
    alertIds: Array<string | ObjectId>,
    newState: EntityEventStateWithoutNew,
  ) => Promise<void>;
  assignAlertToMyself: (alertId: string | ObjectId) => Promise<void>;
  resolveAlert: (alertId: string | ObjectId) => Promise<void>;
}

interface MonitorState {
  eventId: ObjectId | null;
  alerts: Map<string, EntityEvent>;
  starterTracksManagement: Comlink.Remote<StarterTracksManagement> | null;
  isLoadingTracks: boolean;
  isReady: boolean;
  selectedStarterAlertMenu: SelectedStarterAlertMenu;
  selectedStarterGraphInfoMenu: SelectedStarterGraphInfoMenu;
  selectedStarterId: string | null;
  mapInstance: mapboxgl.Map | null;
  actions: MonitorActions;
  lock: boolean;
  proxy: Comlink.Remote<StarterTracksManagement> | null;
}

export const useMonitorStore = create<MonitorState>((set, get) => ({
  eventId: null,
  alerts: new Map(),
  starterTracksManagement: null,
  isLoadingTracks: false,
  isReady: false,
  selectedStarterAlertMenu: 'starters',
  selectedStarterGraphInfoMenu: 'graph',
  selectedStarterId: null,
  mapInstance: null,
  lock: false,
  proxy: null,
  actions: {
    setEventId: async (eventIdOrSlug) => {
      if (ObjectId.isValid(eventIdOrSlug)) {
        set({ eventId: new ObjectId(eventIdOrSlug) });
      }

      const event = await apiClient.getEvent(eventIdOrSlug.toString());
      if (event == null) {
        throw new Error('Event not found');
      }

      set({ eventId: new ObjectId(event.id) });
    },
    setMapInstance: (mapInstance: mapboxgl.Map | null) => set({ mapInstance }),
    flyTo: async (target: Target) => {
      const { mapInstance } = get();
      if (mapInstance == null) {
        throw new Error('Map instance is not set');
      }

      mapInstance.flyTo({
        center: target.center,
        zoom: target.zoom,
        bearing: 0,
        pitch: 0,
        duration: 1000,
        essential: false,
      });
      return Promise.race([
        new Promise<void>((resolve) => {
          mapInstance.once('moveend', () => {
            resolve();
          });
        }),
        sleep(1000),
      ]);
    },
    initStarterTracksManagement: async () => {
      const lock = get().lock;
      if (lock) {
        console.warn('StarterTracksManagement is already initialized');
        return;
      }
      set({ lock: true });

      try {
        const { starterTracksManagement, eventId } = get();
        if (eventId == null || !ObjectId.isValid(eventId)) {
          throw new Error('Invalid event ID');
        }

        if (starterTracksManagement != null) {
          return;
        }

        if (window.Worker == null) {
          throw new Error('Web Workers are not supported');
        }

        const remoteClass = Comlink.wrap<StarterTracksManagement>(
          new Worker(new URL('../../worker/starter-tracks-management.ts', import.meta.url), {
            type: 'module',
          }),
        );

        // @ts-expect-error Comlink typings are incorrect
        const management = (await new remoteClass({
          eventId: eventId.toHexString(),
          host: apiClient._host,
        })) as Comlink.Remote<StarterTracksManagement>;

        // @ts-expect-error Comlink typings are incorrect
        management.onReady = Comlink.proxy(async () => set({ isReady: true }));
        // @ts-expect-error Comlink typings are incorrect
        management.onUpdateStart = Comlink.proxy(async () => set({ isLoadingTracks: true }));
        // @ts-expect-error Comlink typings are incorrect
        management.onUpdateFinish = Comlink.proxy(() => set({ isLoadingTracks: false }));
        set({ starterTracksManagement: management, proxy: management });

        await management.init();
        console.log('StarterTracksManagement initialized');
      } catch (error) {
        console.error('Failed to initialize StarterTracksManagement', error);
      } finally {
        set({ lock: false });
      }
    },
    closeStarterTracksManagement: async () => {
      const { starterTracksManagement, proxy } = get();
      if (starterTracksManagement == null || proxy == null) {
        console.warn('StarterTracksManagement is not initialized');
        return;
      }
      starterTracksManagement.terminate();
      proxy[Comlink.releaseProxy]();

      set({ starterTracksManagement: null, proxy: null, isReady: false });
      console.log('StarterTracksManagement closed');
    },
    updateStarterTracks: async () => {
      try {
        const { starterTracksManagement } = get();
        if (starterTracksManagement == null) {
          throw new Error('StarterTracksManagement is not initialized');
        }

        await starterTracksManagement.update();
      } catch (error) {
        console.error('Failed to update starter tracks', error);
      }
    },
    getStarterTrack: async (starterId) => {
      const { starterTracksManagement } = get();
      if (starterTracksManagement == null) {
        throw new Error('StarterTracksManagement is not initialized');
      }

      return starterTracksManagement.getStarterTrack(starterId);
    },
    getFrontendGeneratedEntityEvents: async () => {
      const { starterTracksManagement } = get();
      if (starterTracksManagement == null) return new Map();
      const events = await starterTracksManagement.getFrontendGeneratedEntityEvents();

      return z.map(z.string(), EntityEventSchema).parse(events);
    },
    setSelectedStarterId: async (starterId: string | null, flyToStarterPosition = true) => {
      const currentStarterId = get().selectedStarterId;
      if (currentStarterId === starterId) {
        set({ selectedStarterId: null });
        return;
      }

      if (flyToStarterPosition && starterId != null) {
        const lastPoints = await get().starterTracksManagement?.getLastPointOfStarter(starterId);
        if (lastPoints != null) {
          const { lng, lat } = lastPoints;
          get().actions.flyTo({ center: [lng, lat], zoom: 14 });
        }
      }

      set({ selectedStarterId: starterId });
    },

    loadAlerts: async () => {
      const eventId = get().eventId;

      if (eventId === null) {
        throw new Error('Event ID is not set');
      }
      // TODO: cache this values
      const event = await apiClient.getEvent(eventId.toString());
      const currentUser = useStore.getState().users.getter.currentUser();

      const previousAlerts = get().alerts;

      const fetchedBackend = await apiClient.getEntityEventsByEvent(eventId, {
        types: basicAlertEventTypes,
        limit: 10000,
      });
      const backendMap = new Map<string, EntityEvent>();
      for (const alert of fetchedBackend) {
        backendMap.set(alert.id.toHexString(), alert);
      }

      let frontendMap = new Map<string, EntityEvent>();
      try {
        const raw = await get().actions.getFrontendGeneratedEntityEvents();
        frontendMap = raw;
      } catch (err) {
        console.warn('Could not load frontend events', err);
      }

      const newAlerts = new Map([...backendMap, ...frontendMap]);

      // at first store the new alerts
      set(
        produce((s: MonitorState) => {
          if (s.alerts.size === 0) {
            s.alerts = new Map(
              Array.from(newAlerts.entries()).map(([_id, alert]) => [getAlertKey(alert), alert]),
            );
            return;
          }

          for (const [, alert] of newAlerts) {
            const key = getAlertKey(alert);
            if (s.alerts.has(key)) {
              s.alerts.set(key, alert);
            } else {
              s.alerts.set(key, alert);
            }
          }
        }),
      );

      // create notifications
      if (previousAlerts.size > 0) {
        for (const [_id, alert] of newAlerts) {
          const key = getAlertKey(alert);
          if (previousAlerts.has(key)) continue;
          if (alert.state === EntityEventState.RESOLVED) continue;
          if (alert.occurredAt.getTime() < Date.now() - 5 * OneMinuteInMillis) continue;

          let starter: RacemapStarter | null = null;
          try {
            switch (alert.entityType) {
              case EntityType.STARTERS:
                starter = await apiClient.getStarter(alert.entityId.toHexString());
                break;
              case EntityType.DEVICES:
                starter = (
                  await apiClient.getEventStarters(eventId.toString(), {
                    deviceId: alert.entityId.toHexString(),
                  })
                )[0];
                break;
            }
          } catch (err) {
            console.warn('Failed to fetch starter info:', err);
          }

          const starterName = starter?.name ?? 'Unknown';
          const starterNumber = starter?.startNumber ?? 'Unknown';

          showAlertNotification({
            alert,
            starterNumber,
            starterName,
            user: currentUser,
            event,
            onAssign: async (alertId) => {
              await get().actions.assignAlertToMyself(alertId);
            },
            onResolve: async (alertId) => {
              await get().actions.resolveAlert(alertId);
            },
          });
        }
      }
    },

    initMonitoring: async (eventSlug: string) => {
      try {
        await get().actions.setEventId(eventSlug);
        await get().actions.loadAlerts();
      } catch (error) {
        console.error('Error initializing alerts:', error);
      }
    },

    updateAlertState: async (
      alertIds: Array<string | ObjectId>,
      newState: EntityEventStateWithoutNew,
    ) => {
      const oldAlerStates: Record<
        string,
        [EntityEvent['state'], EntityEvent['stateEditedBy'], EntityEvent['stateEditedAt']]
      > = {};
      const currentUser = useStore.getState().users.getter.currentUser();

      try {
        // optimistically update the state in the store
        set(
          produce((s: MonitorState) => {
            for (const alertId of alertIds) {
              const alert = Array.from(s.alerts.values()).find(
                (a) => a.id.toString() === alertId.toString(),
              );
              if (alert == null) continue;

              const key = getAlertKey(alert);
              oldAlerStates[key] = [alert.state, alert.stateEditedBy, alert.stateEditedAt];
              alert.state = newState;
              alert.stateEditedBy = currentUser != null ? new ObjectId(currentUser?.id) : null;
              alert.stateEditedAt = new Date();
            }
          }),
        );
        console.log('Optimistically updated alert state', alertIds, newState);

        const updatedAlerts = await apiClient.updateEntityEventState(alertIds, newState);

        set(
          produce((s: MonitorState) => {
            updatedAlerts.forEach((alert) => s.alerts.set(getAlertKey(alert), alert));
          }),
        );
        console.log('Updated alert state', updatedAlerts);
      } catch (error) {
        console.error('Failed to update alert state', error);
        set(
          produce((s: MonitorState) => {
            for (const alertId of alertIds) {
              const alert = Array.from(s.alerts.values()).find(
                (a) => a.id.toString() === alertId.toString(),
              );
              if (alert == null) continue;
              const oldValues = oldAlerStates[getAlertKey(alert)];
              if (oldValues == null) continue;
              alert.state = oldValues[0];
              alert.stateEditedBy = oldValues[1];
              alert.stateEditedAt = oldValues[2];
            }
          }),
        );
      }
    },

    assignAlertToMyself: async (alertId: string | ObjectId) => {
      await get().actions.updateAlertState([alertId], EntityEventState.IN_PROGRESS);
    },

    resolveAlert: async (alertId: string | ObjectId) => {
      await get().actions.updateAlertState([alertId], EntityEventState.RESOLVED);
    },
  },
}));

const getAlertKey = (alert: EntityEvent) =>
  `${alert.entityType}-${alert.entityId.toHexString()}-${alert.occurredAt.toISOString()}`;

export const useSelectedStarterAlertMenu = () =>
  useMonitorStore((state) => state.selectedStarterAlertMenu);
export const useSelectedStarterGraphInfoMenu = () =>
  useMonitorStore((state) => state.selectedStarterGraphInfoMenu);
export const useSelectedStarterId = () => useMonitorStore((state) => state.selectedStarterId);
export const useMonitorActions = () => useMonitorStore((state) => state.actions);
export const useEventId = () => useMonitorStore((state) => state.eventId);
export const useAlerts = () =>
  useMonitorStore(useShallow((state) => Array.from(state.alerts.values())));
